/**
 * Admin 操作ログ — list of admin mutations with Japan Standard Time timestamps.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../config';
import { adminFetch } from '../../../../utils/adminFetch';
import './AdminActivityLog.css';

type LogItem = {
  id: number;
  occurredAt: string;
  adminEmail: string;
  adminName: string | null;
  actionCode: string;
  actionLabelJa: string;
  empEmail: string | null;
  empName: string | null;
  invitationId: string | null;
  summaryJa: string;
};

const ACTION_OPTIONS: { code: string; label: string }[] = [
  { code: '', label: 'すべて' },
  { code: 'EMP_REGISTERED', label: '従業員登録' },
  { code: 'EMP_UPDATED', label: '従業員情報更新' },
  { code: 'EMP_DELETED', label: '従業員削除' },
  { code: 'INVITATION_SENT', label: '招待送信' },
  { code: 'INVITATION_UPDATED', label: '招待内容変更' },
  { code: 'DOCUMENT_APPROVED', label: '書類承認' },
  { code: 'DOCUMENT_DISAPPROVED', label: '書類否認' },
  { code: 'FORM_APPROVED', label: '記入書類承認' },
  { code: 'FORM_DISAPPROVED', label: '記入書類否認' },
  { code: 'PROFILE_APPROVED', label: 'プロフィール承認' },
  { code: 'PROFILE_DISAPPROVED', label: 'プロフィール否認' },
  { code: 'SUBMISSION_ADMIN_EDITED', label: '提出内容の管理者修正' },
  { code: 'RESUBMISSION_REQUESTED', label: '再提出依頼' },
  { code: 'SUBMISSION_CLEARED', label: '提出完了（承認通知）' },
  { code: 'EMPLOYEE_LEFT', label: '退職処理' },
  { code: 'EMPLOYEE_REJOIN_PREPARED', label: '再雇用準備' },
  { code: 'CHAT_MESSAGE_SENT', label: 'メッセージ送信' },
  { code: 'CHAT_MESSAGE_EDITED', label: 'メッセージ編集' },
  { code: 'CHAT_MESSAGE_DELETED', label: 'メッセージ削除' },
  { code: 'CHAT_CONVERSATION_ARCHIVED', label: 'メッセージ会話アーカイブ' },
];

const AdminActivityLog: React.FC = () => {
  const [items, setItems] = useState<LogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [adminEmail, setAdminEmail] = useState('');
  const [empEmail, setEmpEmail] = useState('');
  const [actionCode, setActionCode] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const load = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(pageNum));
      params.set('size', '50');
      if (adminEmail.trim()) params.set('adminEmail', adminEmail.trim());
      if (empEmail.trim()) params.set('empEmail', empEmail.trim());
      if (actionCode) params.set('actionCode', actionCode);
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate) params.set('toDate', toDate);

      const res = await adminFetch(`${API_BASE_URL}/api/admin/activity-log?${params}`);
      if (!res.ok) {
        throw new Error(`読み込みに失敗しました（${res.status}）`);
      }
      const data = await res.json();
      setItems(Array.isArray(data.content) ? data.content : []);
      setPage(typeof data.page === 'number' ? data.page : pageNum);
      setTotalPages(typeof data.totalPages === 'number' ? data.totalPages : 0);
      setTotalElements(typeof data.totalElements === 'number' ? data.totalElements : 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : '読み込みに失敗しました');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [adminEmail, empEmail, actionCode, fromDate, toDate]);

  useEffect(() => {
    void load(0);
  }, []); // initial load only

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void load(0);
  };

  return (
    <div className="admin-activity-log">
      <h2 className="admin-activity-log__title">操作ログ</h2>

      <form className="admin-activity-log__filters" onSubmit={handleSearch}>
        <label>
          管理者メール
          <input
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            placeholder="例: admin@example.com"
          />
        </label>
        <label>
          従業員メール
          <input
            type="email"
            value={empEmail}
            onChange={(e) => setEmpEmail(e.target.value)}
            placeholder="例: emp@example.com"
          />
        </label>
        <label>
          操作
          <select value={actionCode} onChange={(e) => setActionCode(e.target.value)}>
            {ACTION_OPTIONS.map((o) => (
              <option key={o.code || 'all'} value={o.code}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          開始日
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          終了日
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="submit" className="admin-activity-log__search-btn" disabled={loading}>
          検索
        </button>
      </form>

      {error && <p className="admin-activity-log__error">{error}</p>}
      {loading && <p className="admin-activity-log__loading">読み込み中…</p>}

      <div className="admin-activity-log__table-wrap">
        <table className="admin-activity-log__table">
          <thead>
            <tr>
              <th>日時</th>
              <th>管理者</th>
              <th>操作</th>
              <th>従業員</th>
              <th>内容</th>
            </tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan={5}>該当するログはありません。</td>
              </tr>
            ) : (
              items.map((row) => (
                <tr key={row.id}>
                  <td className="admin-activity-log__mono">{row.occurredAt}</td>
                  <td>
                    {row.adminName ? `${row.adminName}` : ''}
                    {row.adminName ? <br /> : null}
                    <span className="admin-activity-log__muted">{row.adminEmail}</span>
                  </td>
                  <td>{row.actionLabelJa}</td>
                  <td>
                    {row.empName || '—'}
                    {row.empEmail ? (
                      <>
                        <br />
                        <span className="admin-activity-log__muted">{row.empEmail}</span>
                      </>
                    ) : null}
                  </td>
                  <td>{row.summaryJa}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="admin-activity-log__pager">
        <span>
          全 {totalElements} 件
        </span>
        <button
          type="button"
          disabled={loading || page <= 0}
          onClick={() => void load(page - 1)}
        >
          前へ
        </button>
        <span>
          {totalPages === 0 ? 0 : page + 1} / {totalPages}
        </span>
        <button
          type="button"
          disabled={loading || page + 1 >= totalPages}
          onClick={() => void load(page + 1)}
        >
          次へ
        </button>
      </div>
    </div>
  );
};

export default AdminActivityLog;
